/**
 * FileGateway：本地文件访问的统一抽象（M1-16 / DESIGN.md §2.6、§4.7）。
 *
 * 浏览器差异（§2.6 的矩阵）决定了必须有三种实现：
 *
 * - `FsaGateway`（Chromium）：File System Access API，能拿到文件句柄，
 *   Ctrl+S 可以**原地覆盖**——桌面应用的体验。
 * - `DownloadGateway`（Firefox/Safari）：只能 `<input type=file>` 进、
 *   `<a download>` 出。没有句柄，每次保存都是导出一份新文件。
 * - `OpfsWorkspace`：源私有文件系统里的内部工作区。用户看不到它，
 *   它的职责是**镜像工作文档**做崩溃恢复（§2.6：工作文档始终镜像一份到 OPFS）。
 *
 * 三种实现共用本接口，也共用同一套接口测试（test/io/gateway.test.ts）。
 */

export type GatewayKind = 'fsa' | 'download' | 'opfs-workspace';

/**
 * 已保存目标的引用。**不透明**：内部如何找回真正的句柄是各实现的事，
 * 调用方只能把它原样传回 `save()`。`null` 语义为"没有可覆盖的目标"。
 */
export interface FileHandleRef {
  readonly gateway: GatewayKind;
  /** 人可读的目标名，仅用于展示（标题栏、最近文件列表）。 */
  readonly name: string;
}

/** `open()` 返回的一项：文件名、完整字节，以及可用于原地覆盖的句柄（若有）。 */
export interface OpenedFile {
  readonly name: string;
  readonly bytes: Uint8Array;
  /** 能原地覆盖的实现（FSA）给出句柄；下载回退给 null。 */
  readonly handle: FileHandleRef | null;
}

export interface SavePayload {
  /** 建议文件名（含扩展名）。 */
  readonly name: string;
  readonly bytes: Uint8Array;
  /** MIME 类型，默认 application/octet-stream。 */
  readonly mimeType?: string;
}

export interface GatewayCallOptions {
  /** open/save 都可能涉及用户交互与磁盘写入（> 16ms），必须可中止。 */
  readonly signal?: AbortSignal;
}

export interface FileGateway {
  readonly kind: GatewayKind;
  /** FSA 可用 → true（DESIGN.md §4.7）。UI 据此决定「保存」还是「下载副本…」（ADR-0016）。 */
  readonly canOverwrite: boolean;
  /**
   * 让用户挑选文件并读入。
   *
   * @param accept 扩展名列表（形如 `'.cvsm'`）。
   * @returns 用户取消时返回**空数组**——取消是正常路径，不是错误。
   */
  open(accept: readonly string[], options?: GatewayCallOptions): Promise<OpenedFile[]>;
  /**
   * 保存。给了 `handle` 且实现支持覆盖时**原地写回**，否则弹出保存框/触发下载。
   *
   * @returns 保存目标的引用（供下次覆盖）；无句柄语义的实现（下载）或用户取消时返回 null。
   */
  save(
    payload: SavePayload,
    handle?: FileHandleRef | null,
    options?: GatewayCallOptions,
  ): Promise<FileHandleRef | null>;
}

/**
 * 保存动作的 UI 文案（验收标准 1 的后半句）。
 *
 * 文案放在 io 层看似越界，实际是刻意的：「能否原地覆盖 → 用什么动词」
 * 是能力矩阵的一部分，三个调用方（菜单、快捷键提示、标题栏）各自翻译
 * 一遍才是重复。UI 层直接用这个函数的返回值。
 *
 * 降级环境的措辞是「下载副本…」而不是「导出为…」（ADR-0016）：这个
 * 动作保存的始终是 `.cvsm` 工程文件，跟真正把画面导出成 PNG/PSD 等
 * 图片格式的「导出」菜单项是两回事——两者原来共用「导出」这个词，
 * 在文件菜单里紧挨着「导出为 PNG…」时会被用户误认成同一类操作。
 */
export function saveActionLabel(gateway: Pick<FileGateway, 'canOverwrite'>): '保存' | '下载副本…' {
  return gateway.canOverwrite ? '保存' : '下载副本…';
}

/** 统一的中止检查。已中止时抛 DOMException('AbortError')，与平台 API 一致。 */
export function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) {
    throw new DOMException('操作已中止', 'AbortError');
  }
}

/** 判断是否为用户取消（picker 关闭）。取消要静默处理，其他错误照常抛。 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
